import React, { useEffect, useMemo, useState } from "react";
import debounce from "lodash.debounce";
import Icon from "../Icon";
import Pager from "../Pager";
import { countdownLabel, formatFriendly, productColor } from "../helpers";
import { FormattedUpload, UploadListQuery, UploadStatus } from "../types";
import StatusPill, { STATUS_LABELS } from "./StatusPill";
import { PageToolbar } from "../../../components/PageToolbar";

interface Props {
  records: FormattedUpload[];
  total: number;
  listQuery: UploadListQuery;
  onListQueryChange: (next: UploadListQuery) => void;
  onOpen: (record: FormattedUpload) => void;
  onRetry: (record: FormattedUpload) => void;
  onDownload: (record: FormattedUpload) => void;
  onRefresh: () => void;
  refreshing: boolean;
}

type FilterKey = "all" | UploadStatus;

const FILTER_KEYS: FilterKey[] = [
  "all",
  "parsing",
  "scheduled",
  "processing",
  "completed",
  "failed",
  "rejected",
  "canceled",
  "reversing",
  "reversed",
];

const filterLabel = (key: FilterKey): string =>
  key === "all" ? "All" : STATUS_LABELS[key];

const SEARCH_DELAY_MS = 300;

const RecordsTable: React.FC<Props> = ({
  records,
  total,
  listQuery,
  onListQueryChange,
  onOpen,
  onRetry,
  onDownload,
  onRefresh,
  refreshing,
}) => {
  const [query, setQuery] = useState(listQuery.search);
  const filter: FilterKey = listQuery.status ?? "all";

  const pushSearch = useMemo(
    () =>
      debounce((search: string, current: UploadListQuery) => {
        if (search === current.search) return;
        onListQueryChange({ ...current, search, page: 1 });
      }, SEARCH_DELAY_MS),
    [onListQueryChange],
  );

  useEffect(() => () => pushSearch.cancel(), [pushSearch]);

  useEffect(() => {
    pushSearch(query, listQuery);
  }, [query, listQuery, pushSearch]);

  useEffect(() => {
    if (listQuery.search !== "") return;
    pushSearch.cancel();
    setQuery("");
  }, [listQuery.search, pushSearch]);

  const setFilter = (next: FilterKey): void =>
    onListQueryChange({
      ...listQuery,
      status: next === "all" ? null : next,
      page: 1,
    });

  return (
    <div className="card" style={{ marginTop: 20, marginBottom: 40 }}>
      <div className="card-head" style={{ justifyContent: "space-between" }}>
        <div>
          <div className="card-title">Recent uploads</div>
          <div className="card-sub">
            Reconciliation runs on its scheduled date. Click any row to see
            what’s happened so far.
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button
            className="btn btn-sm"
            onClick={onRefresh}
            disabled={refreshing}
          >
            <Icon
              name="refresh"
              size={14}
              style={
                refreshing
                  ? { animation: "spin 0.8s linear infinite" }
                  : undefined
              }
            />{" "}
            Refresh
          </button>
        </div>
      </div>
      <PageToolbar
        searchValue={query}
        onSearchChange={(event) => setQuery(event.target.value)}
        searchPlaceholder="Search by file, ID or uploader…"
        filters={
          <PageToolbar.Filters
            isFiltering={filter !== "all" || !!query}
            onClear={() => {
              pushSearch.cancel();
              setQuery("");
              onListQueryChange({
                ...listQuery,
                status: null,
                search: "",
                page: 1,
              });
            }}
            configs={[
              {
                label: "Status",
                options: FILTER_KEYS.map((key) => ({
                  label: filterLabel(key),
                  value: key,
                })),
                selectedValues: filter === "all" ? [] : [filter],
                onChange: (values) => {
                  if (values.length === 0) {
                    setFilter("all");
                  } else {
                    setFilter(values[0] as FilterKey);
                  }
                },
                mode: "single",
              },
            ]}
          />
        }
      />
      <div className="tbl-wrap">
        {refreshing && records.length === 0 ? (
          <div className="empty">
            <div className="empty-illustration">
              <Icon
                name="refresh"
                size={28}
                strokeWidth={2}
                style={{ animation: "spin 0.8s linear infinite" }}
              />
            </div>
            <div className="empty-title">Loading uploads…</div>
            <div>Fetching the latest payment statement uploads.</div>
          </div>
        ) : records.length === 0 ? (
          <div className="empty">
            <div className="empty-illustration">
              <Icon name="inbox" size={36} strokeWidth={1.5} />
            </div>
            <div className="empty-title">Nothing here yet</div>
            <div>
              Clear your filters, or upload a statement above to get started.
            </div>
          </div>
        ) : (
          <table className="tbl">
            <thead>
              <tr>
                <th>File</th>
                <th>Products</th>
                <th>Period</th>
                <th>Uploaded</th>
                <th>Status</th>
                <th style={{ textAlign: "right" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {records.map((record) => (
                <tr
                  key={record.id}
                  style={{ cursor: "pointer" }}
                  onClick={() => onOpen(record)}
                >
                  <td>
                    <div className="filename">
                      <div className="file-ico">
                        <Icon name="file-spreadsheet" size={16} />
                      </div>
                      <div>
                        <div>{record.filename}</div>
                        <div className="meta mono">
                          {record.id} · {record.size}
                        </div>
                        {record.status === "rejected" && (
                          <div className="row-sub danger">
                            {record.validationErrorCount || 0} problem
                            {record.validationErrorCount === 1 ? "" : "s"} found
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td style={{ maxWidth: 320 }}>
                    {record.products && record.products.length > 0 ? (
                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 4,
                          alignItems: "center",
                        }}
                      >
                        {record.products.slice(0, 2).map((product, index) => (
                          <span key={index} className="product-chip">
                            <span
                              className="product-dot"
                              style={{ background: productColor(index) }}
                            />
                            {product.name}
                          </span>
                        ))}
                        {record.products.length > 2 && (
                          <span className="product-chip more">
                            +{record.products.length - 2} more
                          </span>
                        )}
                      </div>
                    ) : (
                      <span
                        style={{ color: "var(--muted)", fontStyle: "italic" }}
                      >
                        —
                      </span>
                    )}
                  </td>
                  <td>{record.period}</td>
                  <td>
                    <div className="row" style={{ gap: 8 }}>
                      <div
                        className="avatar"
                        style={{ width: 24, height: 24, fontSize: 10 }}
                      >
                        {record.uploadedBy.initials}
                      </div>
                      <div>
                        <div style={{ fontSize: 13 }}>
                          {record.uploadedBy.name}
                        </div>
                        <div
                          className="meta"
                          style={{ color: "var(--muted)", fontSize: 11.5 }}
                        >
                          {formatFriendly(record.uploadedAt, true)}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <StatusPill status={record.status} />
                    {record.status === "scheduled" &&
                      record.scheduledFor &&
                      (() => {
                        const countdown = countdownLabel(record.scheduledFor);
                        return (
                          <div className="schedule-meta">
                            Runs{" "}
                            <strong>
                              {formatFriendly(record.scheduledFor)}
                            </strong>{" "}
                            <span className={`countdown ${countdown.cls}`}>
                              {countdown.text}
                            </span>
                            {record.rescheduledAt && (
                              <div
                                style={{
                                  marginTop: 2,
                                  color: "var(--muted)",
                                  fontSize: 11,
                                }}
                              >
                                <Icon
                                  name="clock"
                                  size={10}
                                  strokeWidth={2}
                                  style={{ verticalAlign: "-1px" }}
                                />{" "}
                                Rescheduled by{" "}
                                {record.rescheduledBy?.name?.split(" ")[0] ||
                                  "analyst"}
                              </div>
                            )}
                          </div>
                        );
                      })()}
                  </td>
                  <td onClick={(event) => event.stopPropagation()}>
                    <div className="row-actions">
                      <button
                        className="icon-btn"
                        title="View history"
                        onClick={() => onOpen(record)}
                      >
                        <Icon name="activity" size={15} />
                      </button>
                      {["completed", "reversing", "reversed"].includes(
                        record.status,
                      ) && (
                        <button
                          className="icon-btn"
                          title="Download statements"
                          onClick={() => onDownload(record)}
                        >
                          <Icon name="download" size={15} />
                        </button>
                      )}
                      {record.status === "failed" && (
                        <button
                          className="icon-btn"
                          title="Try again"
                          onClick={() => onRetry(record)}
                        >
                          <Icon name="refresh" size={15} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {records.length > 0 && (
        <Pager
          page={listQuery.page}
          pageSize={listQuery.pageSize}
          total={total}
          shownCount={records.length}
          onPageChange={(page) => onListQueryChange({ ...listQuery, page })}
          onPageSizeChange={(pageSize) =>
            onListQueryChange({ ...listQuery, pageSize, page: 1 })
          }
        />
      )}
    </div>
  );
};

export default RecordsTable;
